import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, X, Circle, Square, ChevronLeft, Loader2, ShieldCheck, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useCamera } from './hooks/useCamera';
import { scoreFrame, snapshotJPEG } from './services/frameQuality';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';

const QUALITY_TICK_MS = 220;   // ~4.5 Hz live border refresh
const FRAME_SAMPLE_MS = 1500;  // sample one frame every 1.5s while recording
const MAX_FRAMES = 60;          // matches backend MAX_FRAMES_PER_SESSION

const LEVEL_RING = {
  red:   'ring-rose-500/80 shadow-[0_0_24px_-4px_rgba(244,63,94,0.65)]',
  amber: 'ring-amber-400/80 shadow-[0_0_28px_-4px_rgba(245,158,11,0.65)]',
  green: 'ring-emerald-500/80 shadow-[0_0_36px_-4px_rgba(16,185,129,0.7)]',
};
const LEVEL_HINT = {
  red:   { text: 'Position a page in the frame', tone: 'text-rose-300' },
  amber: { text: 'Hold steady · improve lighting', tone: 'text-amber-300' },
  green: { text: 'Looking great — keep capturing', tone: 'text-emerald-300' },
};

export default function BoscoCaptureCameraView() {
  const navigate = useNavigate();
  const cam = useCamera();
  const [permissionAsked, setPermissionAsked] = useState(false);
  const [quality, setQuality] = useState({ score: 0, level: 'red', brightness: 0, sharpness: 0, hindrance: { present: false, kind: 'none', coverage: 0 } });
  const [recording, setRecording] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [framesCount, setFramesCount] = useState(0);
  const [finalising, setFinalising] = useState(false);
  const sampleTimerRef = useRef(null);
  const qualityTimerRef = useRef(null);
  const sessionRef = useRef(null); // mirrors sessionId for timer closures
  const recordingRef = useRef(false); // mirrors recording for unmount cleanup

  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);
  useEffect(() => { recordingRef.current = recording; }, [recording]);

  // v90.2 — Declarative srcObject binding. The <video> element is only
  // mounted AFTER cam.status flips to 'granted', which happens after
  // getUserMedia resolves. Doing the srcObject assignment inside the
  // hook created a null-ref race (video not yet in the tree) → the
  // reported "black viewfinder with red frame" bug. This effect binds
  // the stream to the video AFTER React has mounted the element.
  useEffect(() => {
    const video = cam.videoRef.current;
    if (!video || !cam.stream) return undefined;
    if (video.srcObject === cam.stream) return undefined;
    video.srcObject = cam.stream;
    // Required for inline playback on iOS Safari — the attribute route
    // is more reliable than the property, especially on older iOS.
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.muted = true;
    const kickPlay = () => { try { video.play().catch(() => {}); } catch { /* ignore */ } };
    if (video.readyState >= 1) kickPlay();
    else video.addEventListener('loadedmetadata', kickPlay, { once: true });
    return () => video.removeEventListener('loadedmetadata', kickPlay);
  }, [cam.stream, cam.videoRef]);

  // Permission gate UI — user must explicitly press a button to request camera.
  const askPermission = useCallback(async () => {
    setPermissionAsked(true);
    await cam.start();   // v90.2 — rear camera is hard-locked in the hook; no arg needed
  }, [cam]);

  // Live quality border ticker
  useEffect(() => {
    if (cam.status !== 'granted') return undefined;
    qualityTimerRef.current = setInterval(() => {
      const q = scoreFrame(cam.videoRef.current);
      if (q.ok) setQuality({
        score: q.score, level: q.level,
        brightness: q.brightness, sharpness: q.sharpness,
        hindrance: q.hindrance || { present: false, kind: 'none', coverage: 0 },
      });
    }, QUALITY_TICK_MS);
    return () => { if (qualityTimerRef.current) clearInterval(qualityTimerRef.current); };
  }, [cam.status, cam.videoRef]);

  // Frame sampler (active only while recording)
  useEffect(() => {
    if (!recording) {
      if (sampleTimerRef.current) { clearInterval(sampleTimerRef.current); sampleTimerRef.current = null; }
      return undefined;
    }
    sampleTimerRef.current = setInterval(async () => {
      const sid = sessionRef.current;
      if (!sid) return;
      const dataUrl = snapshotJPEG(cam.videoRef.current);
      if (!dataUrl) return;
      const q = scoreFrame(cam.videoRef.current);
      try {
        const token = localStorage.getItem(TOKEN_KEY) || '';
        const r = await fetch(`${API_URL}/api/bosco-capture/session/${sid}/frame`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            imageBase64: dataUrl,
            quality: q.ok ? q : { score: 0, level: 'red', brightness: 0, sharpness: 0 },
          }),
        });
        if (r.ok) {
          const d = await r.json();
          setFramesCount(d.framesCount);
          if (d.framesCount >= MAX_FRAMES) {
            toast.info('Frame limit reached — stopping automatically.');
            await onStop({ silent: true });
          }
        } else if (r.status === 429) {
          toast.info('Frame limit reached.');
          await onStop({ silent: true });
        }
      } catch (e) {
        // best-effort; we don't want network blips to interrupt recording
      }
    }, FRAME_SAMPLE_MS);
    return () => { if (sampleTimerRef.current) clearInterval(sampleTimerRef.current); };
  }, [recording]);

  // ─── Buttons ─────────────────────────────────────────────────────────────
  const onStart = useCallback(async () => {
    if (cam.status !== 'granted') {
      await askPermission(); return;
    }
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const r = await fetch(`${API_URL}/api/bosco-capture/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          device: navigator.userAgent.slice(0, 60),
          orientation: 'portrait',
          facing: cam.facing,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail?.message || `Could not start session (${r.status})`);
      }
      const data = await r.json();
      setSessionId(data.sessionId);
      setFramesCount(0);
      setRecording(true);
    } catch (e) {
      toast.error(e.message || 'Could not start recording.');
    }
  }, [cam.status, cam.facing, askPermission]);

  const onStop = useCallback(async ({ silent = false } = {}) => {
    if (!sessionRef.current) { setRecording(false); return; }
    setRecording(false);
    setFinalising(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const r = await fetch(`${API_URL}/api/bosco-capture/session/${sessionRef.current}/finalize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail?.message || `Finalize failed (${r.status})`);
      }
      const data = await r.json();
      if (!silent) {
        toast.success(`${data.framesCount} frame${data.framesCount === 1 ? '' : 's'} captured. Understanding…`);
      }
      // Phase 3 — hand off to the understanding review page.
      navigate(`/bosco-capture/understanding/${sessionRef.current}`);
    } catch (e) {
      toast.error(e.message || 'Could not finalize recording.');
    } finally {
      setFinalising(false);
    }
  }, [navigate]);

  const onCancel = useCallback(async () => {
    setRecording(false);
    const sid = sessionRef.current;
    if (sid) {
      try {
        const token = localStorage.getItem(TOKEN_KEY) || '';
        await fetch(`${API_URL}/api/bosco-capture/session/${sid}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }
    cam.stop();
    navigate('/teacher-assignments');
  }, [cam, navigate]);

  // Unmount safety — abandon any open recording without leaking media.
  useEffect(() => () => {
    const sid = sessionRef.current;
    if (sid && recordingRef.current) {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      // best-effort fire-and-forget
      try {
        navigator.sendBeacon?.(
          `${API_URL}/api/bosco-capture/session/${sid}`,
          new Blob([JSON.stringify({})], { type: 'application/json' }),
        );
      } catch { /* ignore */ }
      try {
        fetch(`${API_URL}/api/bosco-capture/session/${sid}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      } catch { /* ignore */ }
    }
    cam.stop();
  }, []);

  const hint = LEVEL_HINT[quality.level] || LEVEL_HINT.red;

  // ─── Render — split by camera state ─────────────────────────────────────
  // Permission prompt
  if (!permissionAsked || cam.status === 'idle' || cam.status === 'prompting') {
    return (
      <PermissionPrompt
        loading={cam.status === 'prompting'}
        onAllow={askPermission}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }

  if (cam.status === 'denied' || cam.status === 'error') {
    return (
      <ErrorScreen
        title={cam.status === 'denied' ? 'Camera blocked' : 'Camera error'}
        body={cam.error || 'Could not access the camera.'}
        showTips={cam.status === 'denied'}
        onRetry={() => cam.start()}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }

  // Active camera UI
  return (
    <div className="fixed inset-0 bg-black flex flex-col" data-testid="bosco-capture-camera">
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="h-9 w-9 rounded-full bg-black/55 backdrop-blur flex items-center justify-center text-white"
          data-testid="bosco-capture-cancel"
          aria-label="Cancel and exit"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="px-3 py-1 rounded-full bg-black/55 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-300 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> BOSCO Capture · Beta
        </div>
        {/* v90.1 — Flip-camera button removed. BOSCO Capture is
             rear-only per product spec (front camera must NEVER be
             requested or activated). A hidden spacer keeps the header
             layout balanced. */}
        <div className="h-9 w-9" aria-hidden="true" />
      </div>

      {/* Video viewport with quality ring */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video
          ref={cam.videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-cover"
          data-testid="bosco-capture-video"
        />

        {/* Quality viewfinder frame — asymmetric inset so the record button never crashes into the frame */}
        <div className={`pointer-events-none absolute left-6 right-6 top-6 bottom-16 sm:left-12 sm:right-12 sm:top-14 sm:bottom-24 rounded-2xl ring-4 transition-all duration-300 ${LEVEL_RING[quality.level]}`}
             data-testid="bosco-capture-quality-frame"
             data-quality={quality.level}>
          {/* Corner brackets for crispness */}
          <span className="absolute -top-1 -left-1 h-6 w-6 border-t-2 border-l-2 border-white/60 rounded-tl-2xl" />
          <span className="absolute -top-1 -right-1 h-6 w-6 border-t-2 border-r-2 border-white/60 rounded-tr-2xl" />
          <span className="absolute -bottom-1 -left-1 h-6 w-6 border-b-2 border-l-2 border-white/60 rounded-bl-2xl" />
          <span className="absolute -bottom-1 -right-1 h-6 w-6 border-b-2 border-r-2 border-white/60 rounded-br-2xl" />
        </div>

        {/* Hint bubble — hindrance takes priority over generic level hint */}
        <div className="absolute top-20 inset-x-0 flex justify-center" data-testid="bosco-capture-hint">
          {quality.hindrance?.present ? (
            <div
              className="px-3 py-1 rounded-full bg-rose-600/95 text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg shadow-rose-900/40"
              data-testid="bosco-capture-hindrance-banner"
              data-hindrance-kind={quality.hindrance.kind}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {quality.hindrance.kind === 'lens'
                ? 'Lens blocked — uncover the camera'
                : 'Move your hand off the page'}
            </div>
          ) : (
            <div className={`px-3 py-1 rounded-full bg-black/55 backdrop-blur text-xs font-medium ${hint.tone}`}>
              {hint.text}
            </div>
          )}
        </div>

        {/* Recording counter */}
        {recording && (
          <div className="absolute top-32 inset-x-0 flex justify-center" data-testid="bosco-capture-counter">
            <div className="px-3 py-1 rounded-full bg-rose-600/90 text-white text-[11px] font-semibold flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-white animate-pulse" />
              REC · {framesCount}/{MAX_FRAMES} frames
            </div>
          </div>
        )}
      </div>

      {/* Bottom control bar — solid dark strip with explicit safe-area padding so the record button never overlaps the viewfinder */}
      <div
        className="relative z-30 bg-black/95 border-t border-white/5 backdrop-blur-xl px-4 pt-8"
        style={{ paddingBottom: 'calc(1.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex items-end justify-around max-w-md mx-auto">
          {/* Cancel */}
          <button
            type="button"
            onClick={onCancel}
            disabled={finalising}
            className="h-14 w-14 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white disabled:opacity-40"
            data-testid="bosco-capture-cancel-btn"
            aria-label="Cancel"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Start / Stop */}
          {!recording ? (
            <button
              type="button"
              onClick={onStart}
              disabled={finalising}
              className="h-20 w-20 rounded-full bg-white text-rose-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30 ring-4 ring-white/30 transition-all hover:scale-105 disabled:opacity-50"
              data-testid="bosco-capture-start-btn"
              aria-label="Start recording"
            >
              <Circle className="h-12 w-12 fill-rose-600 text-rose-600" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onStop()}
              disabled={finalising}
              className="h-20 w-20 rounded-full bg-white text-rose-600 flex items-center justify-center shadow-2xl shadow-rose-500/40 ring-4 ring-rose-400/40 transition-all hover:scale-105"
              data-testid="bosco-capture-stop-btn"
              aria-label="Stop recording"
            >
              {finalising
                ? <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
                : <Square className="h-9 w-9 fill-rose-600 text-rose-600" />}
            </button>
          )}

          {/* Quality readout */}
          <div className="h-14 w-14 rounded-full bg-white/10 backdrop-blur flex flex-col items-center justify-center text-white text-[10px]" data-testid="bosco-capture-score">
            <div className="font-bold text-base leading-none">{quality.score}</div>
            <div className="opacity-70 uppercase tracking-wider text-[8px] mt-0.5">{quality.level}</div>
          </div>
        </div>

        {/* Privacy strip */}
        <div className="mt-5 text-center text-[10px] text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3 w-3" />
          Frames are processed temporarily and auto-deleted after generation.
        </div>
      </div>
    </div>
  );
}


// ─── Subcomponents ───────────────────────────────────────────────────────────

function PermissionPrompt({ loading, onAllow, onCancel }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-950 flex items-center justify-center p-6"
         data-testid="bosco-capture-permission-prompt">
      <div className="max-w-md text-center">
        <div className="inline-flex h-16 w-16 rounded-2xl bg-emerald-600 text-white items-center justify-center mb-5 shadow-lg shadow-emerald-600/30">
          <Camera className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold mb-2">Camera access for BOSCO Capture™</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          BOSCO needs your camera to capture educational pages. Your media is
          processed temporarily and <strong>auto-deleted</strong> after generation —
          we never store images of textbook content.
        </p>
        <div className="mt-6 flex items-center gap-2 justify-center">
          <Button variant="outline" onClick={onCancel} data-testid="bosco-capture-permission-cancel">Not now</Button>
          <Button
            onClick={onAllow}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bosco-capture-permission-allow"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Allow camera'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ title, body, onRetry, onCancel, showTips }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
         data-testid="bosco-capture-error">
      <div className="max-w-md text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-rose-500/20 text-rose-300 items-center justify-center mb-4">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>

        {/* v90.1 — Phone-settings tips shown when the OS blocked the
            camera. Written in the second person because the teacher
            has to leave the browser to fix it. */}
        {showTips && (
          <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3 text-left text-[11px] text-slate-400 leading-relaxed"
               data-testid="bosco-capture-error-tips">
            <div className="font-semibold text-slate-300 mb-1">How to fix on your phone</div>
            <ul className="space-y-1 list-disc pl-4">
              <li><b>iPhone (Safari)</b> — Settings → Safari → Camera → Allow. Then reload this page.</li>
              <li><b>Android (Chrome)</b> — Tap the padlock ⓘ in the address bar → Permissions → Camera → Allow. Then reload.</li>
              <li>Make sure no other app (WhatsApp, Zoom, Instagram) is currently holding the camera.</li>
              <li>If you use a privacy blocker, allow camera access for this site.</li>
            </ul>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 justify-center">
          <Button variant="outline" onClick={onCancel} data-testid="bosco-capture-error-cancel">Back</Button>
          <Button onClick={onRetry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bosco-capture-error-retry">
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}
